import { useEffect, useState, type FormEvent } from 'react';
import { createLifeNote, deleteLifeNote, KEEPALIVE, listLifeNotes, updateLifeNote } from '../lifeos/api.js';
import { firstLine, humanize, since } from '../lifeos/format.js';
import FormSheet from '../lifeos/FormSheet.js';
import { options, tagList, text, toValues } from '../lifeos/forms.js';
import { sphereColor, useSpheres } from '../lifeos/spheres.js';
import { errorText, useToast } from '../lifeos/toast.js';
import { SafeLink } from '../lifeos/ui.js';
import { useLoad } from '../lifeos/useLoad.js';
import { Button, Chip, EmptyState, Group, Icon, IconButton, Loader, MoreRow, Page, Row, Meta, PickSheet, SearchField, Sheet, useParamSetter } from '../ui/index.js';
import type { LifeNote, LifeNoteKind } from '../lifeos/types.js';

/** Source filter chips: everything, the owner's own notes, or the indexed Obsidian vault. */
export type NoteSource = 'all' | 'mine' | 'obsidian';
const SOURCES: readonly { value: NoteSource; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'mine', label: 'Mine' },
  { value: 'obsidian', label: 'Obsidian' },
];

export function isObsidian(n: Pick<LifeNote, 'source'>): boolean {
  return n.source === 'obsidian';
}

/** 'Work/Rova/plan.md' (or 'obsidian:Work/Rova/plan.md') → 'Work/Rova'; '' at the vault root. */
export function obsidianFolder(ref: string | null | undefined): string {
  if (!ref) return '';
  const path = ref.replace(/^obsidian:\/*/, '');
  const i = path.lastIndexOf('/');
  return i > 0 ? path.slice(0, i) : '';
}

const KINDS: LifeNoteKind[] = ['idea', 'quote', 'link', 'snippet', 'inspiration', 'reference', 'memory', 'other'];

/**
 * Ideas & notes (`/notes`): the Life OS notes table (`/api/life-notes`), not
 * the medical notes on /records. Capture first, search second.
 */
/** Notes shown before "Show N more". */
const NOTES_SHOWN = 12;

export default function Notes(): JSX.Element {
  const [params, setParam] = useParamSetter();
  const q = params.get('q') ?? '';
  const kind = params.get('kind');
  const tag = params.get('tag');
  const source: NoteSource = params.get('source') === 'mine' || params.get('source') === 'obsidian' ? (params.get('source') as NoteSource) : 'all';
  const [search, setSearch] = useState(q);
  const notes = useLoad(
    () => listLifeNotes({ q: q || undefined, kind: kind ?? undefined, tag: tag ?? undefined }),
    [q, kind, tag],
  );
  const spheres = useSpheres();
  const toast = useToast();
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const [editing, setEditing] = useState<LifeNote | null>(null);
  const [picking, setPicking] = useState(false);
  const [adding, setAdding] = useState(false);
  const [all, setAll] = useState(false);

  // Search as you type, a beat after the last key.
  useEffect(() => {
    const t = setTimeout(() => {
      if (search.trim() !== q) setParam('q', search.trim() || null);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  function remove(n: LifeNote): void {
    const unhide = (): void =>
      setHidden((s) => {
        const x = new Set(s);
        x.delete(n.id);
        return x;
      });
    setHidden((s) => new Set(s).add(n.id));
    toast.defer({
      text: `Deleted “${firstLine(n.title ?? n.body, 40)}”`,
      commit: () => deleteLifeNote(n.id, KEEPALIVE),
      onUndo: unhide,
      onDone: () => void notes.reload().finally(unhide),
      onError: unhide,
      failText: "Couldn't delete that note",
    });
  }

  return (
    <Page
      title="Ideas & notes"
      actions={
        <>
          <IconButton icon="filter" label="Filter by kind" active={kind !== null} onClick={() => setPicking(true)} />
          <IconButton icon="plus" label="New note" onClick={() => setAdding(true)} />
        </>
      }
    >
      <SearchField value={search} onChange={setSearch} placeholder="Search notes" label="Search notes" />
      <div className="ui-chips" role="group" aria-label="Source">
        {SOURCES.map((o) => (
          <Chip key={o.value} on={source === o.value} onClick={() => setParam('source', o.value === 'all' ? null : o.value)}>
            {o.label}
          </Chip>
        ))}
      </div>
      {(kind || tag) && (
        <div className="ui-chips" aria-label="Active filters">
          {kind && (
            <Chip on onClick={() => setParam('kind', null)} label={`Clear kind ${humanize(kind)}`}>
              {humanize(kind)}
              <Icon name="x" size={14} />
            </Chip>
          )}
          {tag && (
            <Chip on onClick={() => setParam('tag', null)} label={`Clear tag ${tag}`}>
              #{tag}
              <Icon name="x" size={14} />
            </Chip>
          )}
        </div>
      )}
      <Loader state={notes}>
        {(items) => {
          const rows = items.filter(
            (n) => !hidden.has(n.id) && (source === 'all' || (source === 'obsidian') === isObsidian(n)),
          );
          if (rows.length === 0) {
            return <EmptyState icon="bulb" title={q || kind || tag || source !== 'all' ? 'No notes match.' : 'No notes yet.'} hint="Tap + to keep an idea, a link or a quote." />;
          }
          const now = new Date();
          const shown = all ? rows : rows.slice(0, NOTES_SHOWN);
          return (
            <Group label="Notes">
              {shown.map((n) => {
                const name = firstLine(n.title ?? n.body, 40);
                const head = n.title ?? firstLine(n.body, 90);
                const body = n.title ? n.body : n.body.length > head.length ? n.body : '';
                const vault = isObsidian(n);
                const folder = vault ? obsidianFolder(n.externalRef) : '';
                return (
                  <Row
                    key={n.id}
                    className="la-note"
                    title={head}
                    sub={
                      <>
                        {body && <span className={`la-note__body${n.kind === 'snippet' ? ' la-note__body--code' : ''}`}>{body}</span>}
                        {n.url && (
                          <span className="la-note__url">
                            <SafeLink href={n.url} />
                          </span>
                        )}
                        <span className="la-note__meta">
                          <Meta color={sphereColor(spheres, n.sphereKey)}>
                            {humanize(n.kind)} · {since(n.createdAt, now)}
                          </Meta>
                          {vault && <span className="notes__src">Obsidian</span>}
                          {folder && <span className="notes__folder">{folder}</span>}
                          {n.tags.map((t) => (
                            <button key={t} type="button" className="la-tag" onClick={() => setParam('tag', t)}>
                              #{t}
                            </button>
                          ))}
                        </span>
                      </>
                    }
                    // Obsidian notes are the vault's: read-only here (the server refuses with 409 anyway).
                    trailing={
                      vault ? undefined : (
                        <button type="button" className="la-edit" onClick={() => setEditing(n)} aria-label={`Edit “${name}”`}>
                          <Icon name="pencil" size={18} />
                        </button>
                      )
                    }
                    swipe={vault ? undefined : { trailing: [{ label: 'Delete', icon: 'trash', tone: 'bad', onAction: () => remove(n) }] }}
                  />
                );
              })}
              <MoreRow count={rows.length - shown.length} onClick={() => setAll(true)} />
            </Group>
          );
        }}
      </Loader>
      {adding && (
        <Sheet title="New note" onClose={() => setAdding(false)}>
          <Capture
            onSaved={() => {
              setAdding(false);
              void notes.reload();
            }}
          />
        </Sheet>
      )}
      {picking && (
        <PickSheet
          title="Show"
          label="Kind"
          value={kind}
          options={[{ key: null, name: 'All kinds' }, ...KINDS.map((k) => ({ key: k, name: humanize(k) }))]}
          onPick={(k) => {
            setParam('kind', k);
            setPicking(false);
          }}
          onClose={() => setPicking(false)}
        />
      )}
      {editing && (
        <FormSheet
          title="Edit note"
          fields={[
            { key: 'title', label: 'Title', wide: true },
            { key: 'body', label: 'Note', kind: 'textarea' },
            { key: 'kind', label: 'Kind', kind: 'select', options: options(KINDS, humanize) },
            { key: 'url', label: 'Link', placeholder: 'https://…' },
            { key: 'tags', label: 'Tags', placeholder: 'comma separated', wide: true },
            { key: 'sphereKey', label: 'Sphere', kind: 'select', options: spheres.map((s) => ({ value: s.key, label: s.name })) },
          ]}
          initial={toValues({ ...editing })}
          danger={{ label: 'Delete note', onClick: () => remove(editing) }}
          onClose={() => setEditing(null)}
          onSubmit={async (v) => {
            await updateLifeNote(editing.id, {
              title: text(v.title),
              body: v.body ?? '',
              kind: v.kind as LifeNoteKind,
              url: text(v.url),
              tags: tagList(v.tags),
              sphereKey: v.sphereKey || undefined,
            });
            void notes.reload();
          }}
        />
      )}
    </Page>
  );
}

/** One textarea; title, link and tags only when asked for. */
function Capture({ onSaved }: { onSaved: () => void }): JSX.Element {
  const toast = useToast();
  const [body, setBody] = useState('');
  const [kind, setKind] = useState<LifeNoteKind>('idea');
  const [more, setMore] = useState(false);
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [tags, setTags] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!body.trim() && !url.trim()) return;
    setBusy(true);
    try {
      await createLifeNote({
        body: body.trim(),
        kind: url.trim() && !body.trim() ? 'link' : kind,
        title: text(title),
        url: text(url),
        tags: tagList(tags),
      });
      setBody('');
      setTitle('');
      setUrl('');
      setTags('');
      setMore(false);
      toast.info('Saved');
      onSaved();
    } catch (err) {
      toast.error(`Couldn't save: ${errorText(err)}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="la-capture" onSubmit={submit} aria-label="Capture a note">
      <textarea
        className="la-capture__body"
        rows={4}
        placeholder="An idea, a quote, a link…"
        aria-label="Note"
        value={body}
        onChange={(e) => setBody(e.target.value)}
      />
      {more && (
        <div className="la-capture__more">
          <input placeholder="Title" aria-label="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
          <input placeholder="Link — https://…" aria-label="Link" value={url} onChange={(e) => setUrl(e.target.value)} />
          <input placeholder="Tags, comma separated" aria-label="Tags" value={tags} onChange={(e) => setTags(e.target.value)} />
        </div>
      )}
      <div className="la-capture__bar">
        <select className="la-capture__kind" aria-label="Kind" value={kind} onChange={(e) => setKind(e.target.value as LifeNoteKind)}>
          {KINDS.map((k) => (
            <option key={k} value={k}>
              {humanize(k)}
            </option>
          ))}
        </select>
        {!more && (
          <button type="button" className="la-capture__toggle" aria-expanded={false} onClick={() => setMore(true)}>
            <Icon name="plus" size={16} />
            Details
          </button>
        )}
        <Button type="submit" size="sm" className="la-capture__save" disabled={busy || (!body.trim() && !url.trim())}>
          Save
        </Button>
      </div>
    </form>
  );
}
